import React, { useId, useMemo } from "react";
import { useInstagramApiPublishingOn } from "../../../../../utils/feature-toggle-utils";

import { RadioGroup, RadioGroupItem } from "../../../../ui/radio-group";
import { FIELDS } from "../../../../../constants/PostFormFields";
import {
  hasApiCapableInstagramAccount,
  type TagCapabilityAccount,
} from "../../PostTags/TagCapabilityWarning";
import {
  isInstagramReelMedia,
  selectedInstagramAccounts,
} from "../InstagramPlacementControl/InstagramPlacementControl";
import type { PostMediaFile } from "../../../../../shared/types/post";
import type { AccountPlatformType } from "../../../../../shared/types/accounts";
import { SwitchFieldRow } from "../SwitchFieldRow/SwitchFieldRow";
import { INSTAGRAM_ONLY } from "../PlatformLabelBadges/PlatformLabelBadges";

const MANUAL = "MANUAL";
const SS_PERFORMANCE = "SS_PERFORMANCE";

interface InstagramTrialReelProps {
  mediaFiles: PostMediaFile[];
  selectedPlatform: AccountPlatformType | "LINKED";
  formAccounts: TagCapabilityAccount[];
  updateField: (field: string, value: unknown) => void;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platformPost?: any;
}

/**
 * SKE-4541. Publishes this reel as a trial reel.
 *
 * Reel-only, because `validateContainerRequest` refuses trial params on any
 * other target with "Trial posts are a reels-only setting.", and that refusal
 * fails the whole post.
 *
 * No client-side eligibility check sits on top of this. Meta answers code 10
 * subcode 2207081 when the account is below the follower threshold, code 10 on
 * its own also means lost permission, and conflating the two once took an
 * entire account off API publishing (SKE-4590). The server disambiguates on the
 * subcode; guessing here would put a second, worse rule in front of it.
 */
export const InstagramTrialReel: React.FC<InstagramTrialReelProps> = ({
  mediaFiles,
  selectedPlatform,
  formAccounts,
  updateField,
  disabled,
  platformPost,
}) => {
  const apiPublishingOn = useInstagramApiPublishingOn();
  const fieldId = useId();
  const manualId = `${fieldId}-manual`;
  const performanceId = `${fieldId}-performance`;

  const igAccounts = useMemo(
    () => selectedInstagramAccounts(formAccounts),
    [formAccounts]
  );

  const show = useMemo(() => {
    if (selectedPlatform !== "IG" && selectedPlatform !== "LINKED")
      return false;
    if (platformPost?.isStory) return false;
    if (!isInstagramReelMedia(mediaFiles)) return false;
    if (igAccounts.length === 0) return false;
    return hasApiCapableInstagramAccount(igAccounts, apiPublishingOn);
  }, [
    selectedPlatform,
    platformPost?.isStory,
    mediaFiles,
    igAccounts,
    apiPublishingOn,
  ]);

  const strategy = platformPost?.instagram?.trialReel?.graduationStrategy;
  const checked = strategy === MANUAL || strategy === SS_PERFORMANCE;

  if (!show) return null;

  const optionClassName = disabled
    ? "flex cursor-not-allowed items-center gap-2 font-inter text-sm leading-5 text-neutral-500"
    : "flex cursor-pointer items-center gap-2 font-inter text-sm leading-5 text-neutral-800";

  return (
    <SwitchFieldRow
      data-testid="ig-trial-reel"
      id={fieldId}
      label="Publish as a trial reel"
      helper="Instagram shows a trial reel only to people who do not follow you. Your followers see it once it graduates."
      checked={checked}
      disabled={disabled}
      selectedPlatform={selectedPlatform}
      platforms={INSTAGRAM_ONLY}
      onCheckedChange={(next: boolean) =>
        updateField(
          FIELDS.IG_TRIAL_REEL,
          next ? { graduationStrategy: MANUAL } : undefined
        )
      }
    >
      {checked ? (
        <div className="flex flex-col gap-2">
          <span
            className="font-inter text-xs font-medium leading-5 text-neutral-600"
            id={`${fieldId}-graduation-label`}
          >
            How it graduates
          </span>
          <RadioGroup
            className="flex flex-col gap-2"
            value={strategy}
            disabled={disabled}
            aria-labelledby={`${fieldId}-graduation-label`}
            onValueChange={(next: string) =>
              updateField(FIELDS.IG_TRIAL_REEL, { graduationStrategy: next })
            }
          >
            <label className={optionClassName} htmlFor={manualId}>
              <RadioGroupItem value={MANUAL} id={manualId} />
              You share it with followers from the Instagram app
            </label>
            <label className={optionClassName} htmlFor={performanceId}>
              <RadioGroupItem value={SS_PERFORMANCE} id={performanceId} />
              Instagram shares it with followers if it performs well
            </label>
          </RadioGroup>
        </div>
      ) : null}
    </SwitchFieldRow>
  );
};

export default InstagramTrialReel;
